<template>
  <div>
    <v-layout v-if="device" align-center class="mb-3">
      <v-breadcrumbs :items="breadcrumbItems" class="pa-0"></v-breadcrumbs>
      <v-spacer></v-spacer>
      <div>       
        <v-btn
          small
          :disabled="device.status != 'online'"
          class="primary--text mr-3"
          outlined
          @click="openSSH"
        >
          <v-icon small class="mr-1">mdi-television</v-icon>
          SSH
        </v-btn>
        <device-operations :device="device" @refresh-device="refresh"></device-operations>
      </div>
    </v-layout>

    <v-layout>
      <page-subtitle
        >View, edit your device details and application allocations
        here.</page-subtitle
      >
    </v-layout>

    <!-- name edit -->
    <v-dialog v-model="dialog" max-width="400px">
      <v-card>
        <v-card-title>
          <span class="headline">Edit device name</span>
        </v-card-title>

        <v-card-text>
          <form v-on:submit.prevent="save()">
            <v-container>
              <v-row>
                <v-col cols="12" sm="12" md="12">
                  <v-text-field
                    v-model="editedItem.name"
                    label="Name"
                  ></v-text-field>
                </v-col>
              </v-row>
            </v-container>
            <v-btn v-show="false" hidden type="submit"></v-btn>
          </form>
        </v-card-text>

        <v-card-text v-if="error">
          <v-alert type="error" outlined>
            {{ error }}
          </v-alert>
        </v-card-text>

        <v-card-actions>
          <v-btn class="primary--text" text @click="close"> Cancel </v-btn>
          <v-spacer></v-spacer>
          <v-btn class="primary--text" text @click="save"> Save </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- version edit -->
    <v-dialog v-model="versionDialog" max-width="600px">
      <v-card>
        <v-card-title>
          <span class="headline">Edit device version</span>
        </v-card-title>

        <v-card-text>
          <p v-if="device && device.info">
            Editing device version will trigger an automatic update.
          </p>
           <div v-if="device && device.info">
            <p>Current version: {{ device.info.agentVersion }}</p>
           </div>
           <div v-if="warningMessage != ''">
            <v-alert
               color="green"
               dismissible
               type="info"
            >{{ warningMessage }}
            </v-alert>
           </div>
          <form v-on:submit.prevent="save()">
            <v-container>
              <v-row>
                <v-col cols="12" sm="12" md="12">
                  <v-select
                    @change="selectedVersion($event)"
                    :items="availableDeviceVersions.versions"
                    v-model="editedItem.desiredAgentVersion"
                    label="Available versions"
                    solo
                  >
                   <template v-slot:selection="{ item }">
                    {{ getVersionName(item) }}
                  </template>
                   <template v-slot:item="{ item }">
                    {{ getVersionName(item) }}
                  </template>
                  </v-select>
                </v-col>
              </v-row>
              <v-row
                v-if="editedItem != undefined && device && device.info != undefined &&
                  editedItem.desiredAgentVersion !== '' &&
                  editedItem.desiredAgentVersion !== device.info.agentVersion"

              >
                <v-col cols="12" sm="12" md="12">
                  <v-alert dense outlined type="info">
                    Device will change version from
                    <strong>{{ device.info.agentVersion }}</strong> to
                    <strong>{{ getVersionName(editedItem.desiredAgentVersion) }}</strong
                    >.
                  </v-alert>
                </v-col>
              </v-row>
            </v-container>
            <v-btn v-show="false" hidden type="submit"></v-btn>
          </form>
        </v-card-text>

        <v-card-text v-if="error">
          <v-alert type="error" outlined>
            {{ error }}
          </v-alert>
        </v-card-text>

        <v-card-actions>
          <v-btn class="primary--text" text @click="close"> Cancel </v-btn>
          <v-spacer></v-spacer>
          <v-btn class="primary--text" text @click="save"> Save </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-layout class="mb-3" wrap>
      <v-flex lg8 md12 sm12 pr-2>
        <v-card v-if="device && device.info" flat outlined height="100%">
          <v-card-title>Device Info</v-card-title>
          <v-card-text>
            <v-list dense>
              <v-list-item v-if="device">
                <v-list-item-content>
                  <span class="font-weight-bold">Device ID</span>
                </v-list-item-content>
                <v-list-item-content class="align-end">
                  {{ device.id }}
                </v-list-item-content>
                <v-list-item-icon>
                  <!-- just for alignment, not editable -->
                </v-list-item-icon>
              </v-list-item>

              <v-list-item v-if="device">
                <v-list-item-content>
                  <span class="font-weight-bold">Device Name</span>
                </v-list-item-content>
                <v-list-item-content class="align-end">
                  {{ device.name }}
                </v-list-item-content>
                <v-list-item-icon>
                  <v-icon small class="mr-2" @click="editName()">
                    mdi-pencil
                  </v-icon>
                </v-list-item-icon>
              </v-list-item>

              <v-list-item v-if="device">
                <v-list-item-content>
                  <span class="font-weight-bold">Device Hostname</span>
                </v-list-item-content>
                <v-list-item-content v-if="device.info" class="align-end">
                  {{ device.info.hostname }}
                </v-list-item-content>
                <v-list-item-content v-else class="align-end">
                  -
                </v-list-item-content>
                <v-list-item-icon>
                  <!-- just for alignment, not editable -->
                </v-list-item-icon>
              </v-list-item>

              <v-list-item v-if="device">
                <v-list-item-content>
                  <span class="font-weight-bold">Device Architecture</span>
                </v-list-item-content>
                <v-list-item-content v-if="device.info" class="align-end">
                  {{ device.info.architecture }}
                </v-list-item-content>
                <v-list-item-content v-else class="align-end">
                  -
                </v-list-item-content>
                <v-list-item-icon>
                  <!-- just for alignment, not editable -->
                </v-list-item-icon>
              </v-list-item>

              <v-list-item v-if="device.info">
                <v-list-item-content>
                  <span class="font-weight-bold">Agent Version</span>
                </v-list-item-content>
                <v-list-item-content class="align-end">
                  <span
                    v-if="
                      device.desiredAgentVersion !== '' &&
                      device.info.agentVersion !== device.desiredAgentVersion
                    "
                  >
                    <!-- Update should be in progress -->
                    {{ device.info.agentVersion }} ->
                    {{ device.desiredAgentVersion }}
                    <v-progress-circular
                      class="ml-2"
                      :width="2"
                      :size="13"
                      color="primary"
                      indeterminate
                    ></v-progress-circular>
                  </span>
                  <span v-else>
                    <!-- updated -->
                    {{ device.info.agentVersion }}
                  </span>
                </v-list-item-content>
                <v-list-item-icon>
                  <!-- just for alignment, not editable -->
                  <v-icon small class="mr-2" @click="editDesiredAgentVersion()">
                    mdi-pencil
                  </v-icon>
                </v-list-item-icon>
              </v-list-item>

              <v-list-item v-if="device.info">
                <v-list-item-content>
                  <span class="font-weight-bold">IP Address</span>
                </v-list-item-content>
                <v-list-item-content class="align-end">
                  {{ device.info.ipAddress }}
                </v-list-item-content>
                <v-list-item-icon>
                  <!-- just for alignment, not editable -->
                  <v-icon small class="mr-2" @click="copyToClipboard(device.info.ipAddress)">mdi-content-copy</v-icon>
                </v-list-item-icon>
              </v-list-item>

              <v-list-item v-if="device && device.info">
                <v-list-item-content>
                  <span class="font-weight-bold">DNS names</span>
                </v-list-item-content>
                <v-list-item-content class="align-end">
                  {{ device.info.projectSubdomain }}
                </v-list-item-content>
                <v-list-item-icon>
                  <!-- just for alignment, not editable -->
                  <v-icon small class="mr-2" @click="copyToClipboard(device.info.projectSubdomain)">mdi-content-copy</v-icon>
                </v-list-item-icon>
              </v-list-item>

              <v-list-item v-if="device.info">
                <v-list-item-content>
                  <span class="font-weight-bold"></span>
                </v-list-item-content>
                <v-list-item-content class="align-end">
                  {{ device.info.deviceSubdomain }}
                </v-list-item-content>
                <v-list-item-icon>
                  <!-- just for alignment, not editable -->
                  <v-icon small class="mr-2" @click="copyToClipboard(device.info.deviceSubdomain)">mdi-content-copy</v-icon>
                </v-list-item-icon>
              </v-list-item>

              <v-list-item v-if="device.info">
                <v-list-item-content>
                  <span class="font-weight-bold">Operating System</span>
                </v-list-item-content>
                <v-list-item-content class="align-end">
                  {{ device.info.osRelease.prettyName }}
                </v-list-item-content>
                <v-list-item-icon>
                  <!-- just for alignment, not editable -->
                </v-list-item-icon>
              </v-list-item>

              <v-list-item v-if="device.info">
                <v-list-item-content>
                  <span class="font-weight-bold">Operating System Family</span>
                </v-list-item-content>
                <v-list-item-content class="align-end">
                  {{ device.info.osRelease.idLike }}
                </v-list-item-content>
                <v-list-item-icon>
                  <!-- just for alignment, not editable -->
                </v-list-item-icon>
              </v-list-item>

              <v-list-item v-if="device.info">
                <v-list-item-content>
                  <span class="font-weight-bold">CPU</span>
                </v-list-item-content>
                <v-list-item-content class="align-end">
                  <v-container class="pl-0" :fluid="true">
                    <v-row no-gutters>
                      <v-col md="4">Name</v-col>
                      <v-col md="8">{{ device.info.cpuInfo.brandName }}</v-col>
                    </v-row>

                    <v-row class="mt-3" no-gutters>
                      <v-col md="4">Physical Cores</v-col>
                      <v-col md="8">{{ device.info.cpuInfo.physicalCores }}</v-col>
                    </v-row>

                    <v-row class="mt-3" no-gutters>
                      <v-col md="4">Threads</v-col>
                      <v-col md="8">{{ device.info.cpuInfo.threadsPerCore }}</v-col>
                    </v-row>

                    <v-row class="mt-3"
                    no-gutters>
                      <v-col md="4">Logical Cores</v-col>
                      <v-col md="8">{{ device.info.cpuInfo.logicalCores }}</v-col>
                    </v-row>
                  </v-container>
                </v-list-item-content>
                <v-list-item-icon>
                  <!-- just for alignment, not editable -->
                </v-list-item-icon>
              </v-list-item>

              <v-list-item v-if="device.info &&
                                 device.info.gpusInfo &&
                                 device.info.gpusInfo.length > 0 &&
                                 device.info.gpusInfo[0].metadata != null">
                <v-list-item-content>
                  <span class="font-weight-bold">GPUs ({{ device.info.gpusInfo.length }})</span>
                </v-list-item-content>
                <v-list-item-content class="align-end">
                  <v-container class="pl-0" :fluid="true">

                    <template
                      v-for="(gpu, i) in device.info.gpusInfo"
                    >
                    <div
                    :key="`vendor-${i}`"
                    v-if="gpu.metadata != undefined || gpu.metadata != null"
                    >
                      <v-row class="mt-3" :key="`vendor-${i}`" no-gutters>
                        <v-col md="4">Name</v-col>
                        <v-col md="8">{{ gpu.productName }}</v-col>
                      </v-row>

                      <v-row class="mt-3" v-if="gpu.metadata['cudaVersion']" :key="`cuda-${i}`" no-gutters>
                        <v-col md="4">CUDA Version</v-col>
                        <v-col md="8">{{ gpu.metadata['cudaVersion'] }}</v-col>
                      </v-row>

                      <v-row class="mt-3" :key="i" no-gutters>
                        <v-col md="4">Memory</v-col>
                        <v-col md="8">{{ gpu.memory }}</v-col>
                      </v-row>
                      <v-row class="mt-3" :key="i" no-gutters>
                        <v-col md="4">Utilization</v-col>
                        <v-col md="4"> GPU: {{ gpu.metadata['gpuUtilization'] }}</v-col>
                        <v-col md="4"> Memory: {{ gpu.metadata['memoryUtilization'] }}</v-col>
                      </v-row>
                    </div>
                    </template>
                  </v-container>
                </v-list-item-content>
                <v-list-item-icon>
                  <!-- just for alignment, not editable -->
                </v-list-item-icon>
              </v-list-item>

              <v-list-item v-if="device.info">
                <v-list-item-content>
                  <span class="font-weight-bold">Added to Synpse</span>
                </v-list-item-content>
                <v-list-item-content class="align-end">
                  {{ device.createdAt | date }}
                </v-list-item-content>
                <v-list-item-icon>
                  <!-- just for alignment, not editable -->
                </v-list-item-icon>
              </v-list-item>

              <v-list-item v-if="device.info">
                <v-list-item-content>
                  <span class="font-weight-bold">Last Seen</span>
                </v-list-item-content>
                <v-list-item-content class="align-end">
                  {{ device.lastSeenAt | date }}
                </v-list-item-content>
                <v-list-item-icon>
                  <!-- just for alignment, not editable -->
                </v-list-item-icon>
              </v-list-item>

              <v-list-item v-if="device.info">
                <v-list-item-content>
                  <span class="font-weight-bold">Status</span>
                </v-list-item-content>
                <v-list-item-content class="align-end">
                  <div class="d-flex flex-column align-end">
                    <div class="d-flex align-center mb-1">
                      <span class="mr-2">{{ device.status }}</span>
                      <v-icon
                        v-if="device.status == 'online'"
                        small
                        color="green accent-3"
                      >fiber_manual_record</v-icon>
                      <v-icon
                        v-else-if="device.status == 'offline'"
                        small
                        color="error"
                      >fiber_manual_record</v-icon>
                    </div>
                    
                    <!-- Update Status Badges -->
                    <div class="d-flex flex-wrap align-center">
                      <v-chip
                        v-if="device.info.deviceUpdatesDisabled"
                        small
                        color="warning"
                        outlined
                        class="mr-1 mb-1"
                      >
                        <v-icon small left>mdi-pause-circle</v-icon>
                        Updates Disabled
                      </v-chip>
                      
                      <v-chip
                        v-if="device.info.deviceDriftStatus === 'drifted'"
                        small
                        color="error"
                        outlined
                        class="mr-1 mb-1"
                      >
                        <v-icon small left>mdi-alert</v-icon>
                        Drifted
                      </v-chip>
                      
                      <v-chip
                        v-if="device.info.deviceDriftStatus === 'inSync'"
                        small
                        color="success"
                        outlined
                        class="mr-1 mb-1"
                      >
                        <v-icon small left>mdi-check-circle</v-icon>
                        In Sync
                      </v-chip>
                      
                      <v-chip
                        v-if="device.info.deviceDriftStatus === 'unknown'"
                        small
                        color="grey"
                        outlined
                        class="mr-1 mb-1"
                      >
                        <v-icon small left>mdi-help-circle</v-icon>
                        Unknown Status
                      </v-chip>
                    </div>
                  </div>
                </v-list-item-content>

                <v-list-item-icon>
                  <!-- Empty icon for alignment -->
                </v-list-item-icon>
              </v-list-item>
            </v-list>
          </v-card-text>
        </v-card>
      </v-flex>
      <v-flex lg4 md12 sm12 pr-2>
        <!-- <v-layout wrap> -->
        <v-card flat outlined  class="">
           <v-card-title>Public URL
           </v-card-title>
           <v-card-text>


             <v-form>
              <v-container>
                <v-row>
                  <v-col
                    cols="12"
                    sm="6"
                  >
                     <v-switch
                        v-model="deviceTunnel"
                        @click="toggleDeviceTunnel"
                        :label="`${deviceTunnel ? 'Enabled' : 'Disabled'}`"
                      ></v-switch>
                  </v-col>

                  <v-col
                    cols="12"
                    sm="6"
                  >
                    <v-text-field
                      v-model="deviceTunnelLocalPort"
                      label="Local Device Port"
                      outlined
                      type="number"
                      :rules="deviceTunnelLocalPortRules"
                    ></v-text-field>
                  </v-col>
                </v-row>
              </v-container>
            </v-form>

            <p v-if="deviceTunnelEnabled">
              <a :href="this.deviceTunnelPublicURL" target="_blank" rel="noopener">
                {{ this.deviceTunnelPublicURL != '' ? this.deviceTunnelPublicURL : '-' }}
              </a>
            </p>

            <v-alert
              dense
              v-if="currentDeviceTunnelLocalPort != deviceTunnelLocalPort"
              outlined
              type="info"
            >
              After modifying local device port, please disable/enable the tunnel to update the configuration.
            </v-alert>

            <p>
              Expose HTTP services running on your device to the internet.
            </p>

           </v-card-text>
        </v-card>

        <v-card v-if="metrics.length > 0" flat outlined class="mt-2">
          <v-card-title>Metrics</v-card-title>
          <v-card-text>

            <vue-frappe
              id="agent_cpu"
              :labels="utilizationMetricLabels"
              title="CPU"
              type="axis-mixed"
              :lineOptions="{
                regionFill: 1,
                hideDots: 1,
              }"
              :height="175"
              :colors="['#e040fb']"
              :tooltipOptions="{
                formatTooltipX: (d) => (d + '').toUpperCase(),
                formatTooltipY: (d) => d + '%',
              }"
              :axisOptions="{
                xIsSeries: true, // default: false
              }"
              :yMarkers="[
                {
                  label: '',
                  value: 100,
                  type: 'solid',
                },
              ]"
              :discreteDomains="false"
              :dataSets="cpuUtilizationMetricChartData"
            >
            </vue-frappe>

            <vue-frappe
              id="agent_ram"
              :labels="utilizationMetricLabels"
              title="Memory"
              type="axis-mixed"
              :lineOptions="{
                regionFill: 1,
                hideDots: 1,
              }"
              :height="175"
              :colors="['#e040fb', '#7cd6fd']"
              :tooltipOptions="{
                formatTooltipX: (d) => (d + '').toUpperCase(),
                formatTooltipY: (d) => d + 'GB',
              }"
              :axisOptions="{
                xIsSeries: true, // default: false
              }"
              :discreteDomains="false"
              :dataSets="ramUtilizationMetricChartData"
            >
            </vue-frappe>

            <vue-frappe
              v-if="gpuUtilizationMetricChartData.length > 0"
              id="gpu_cpu"
              :labels="utilizationMetricLabels"
              title="GPU"
              type="axis-mixed"
              :lineOptions="{
                regionFill: 1,
                hideDots: 1,
              }"
              :height="175"
              :colors="['#e040fb', '#7cd6fd']"
              :tooltipOptions="{
                formatTooltipX: (d) => (d + '').toUpperCase(),
                formatTooltipY: (d) => d + '%',
              }"
              :axisOptions="{
                xIsSeries: true, // default: false
              }"
              :discreteDomains="false"
              :dataSets="gpuUtilizationMetricChartData"
            >
            </vue-frappe>

            <vue-frappe
              v-if="gpuMemoryUtilizationMetricChartData.length > 0"
              id="gpu_ram"
              :labels="utilizationMetricLabels"
              title="GPU Memory"
              type="axis-mixed"
              :lineOptions="{
                regionFill: 1,
                hideDots: 1,
              }"
              :height="175"
              :colors="['#e040fb', '#7cd6fd']"
              :tooltipOptions="{
                formatTooltipX: (d) => (d + '').toUpperCase(),
                formatTooltipY: (d) => d + 'MiB'
              }"
              :axisOptions="{
                xIsSeries: true, // default: false
              }"
              :discreteDomains="false"
              :dataSets="gpuMemoryUtilizationMetricChartData"
            >
            </vue-frappe>

          </v-card-text>
        </v-card>
        <v-card v-else flat outlined>
          <v-card-text>CPU metrics are not available.</v-card-text>
        </v-card>
        <!-- </v-layout> -->
      </v-flex>
    </v-layout>

    <v-layout class="mb-3" wrap>
      <v-flex lg8 md12 sm12 pr-2>
        <device-labels-edit v-if="device" :device="device"></device-labels-edit>
      </v-flex>
    </v-layout>

    <v-layout class="mb-3" wrap>
      <v-flex lg8 md12 sm12 pr-2>
        <device-env-vars-edit
          v-if="device"
          :device="device"
        ></device-env-vars-edit>
      </v-flex>
    </v-layout>

    <v-layout class="mb-3" wrap>
      <v-flex lg8 md12 sm12 pr-2>
        <v-card outlined>
          <v-card-title class="text-subtitle-2">Applications

              <v-spacer></v-spacer>
              <v-tooltip left>
                <template v-slot:activator="{ on: tooltip }">
                  <v-btn
                    icon
                    v-on="tooltip"
                    href="https://docs.synpse.net/synpse-core/applications/deploy"
                    target="_blank"
                    rel="noopener"
                  >
                    <v-icon color="grey" small>help</v-icon>
                  </v-btn>
                </template>
                <span>If device matches application scheduling criteria - allocation will be created</span>
              </v-tooltip>
            </v-card-title>

            <v-card-text>
          <device-applications v-if="device" :device="device">
          </device-applications>
          </v-card-text>
        </v-card>
      </v-flex>
    </v-layout>

    <v-layout class="mb-3" wrap>
      <v-flex lg8 md12 sm12 pr-2>
        <v-card
          flat
          outlined
        >
          <v-expansion-panels>
            <v-expansion-panel>
              <v-expansion-panel-header>
                Device details
              </v-expansion-panel-header>
              <v-expansion-panel-content id="innerExPan">
                <v-list-item v-if="device.info">
                  <v-list-item-content>
                    <span class="text-caption">Container runtime</span>
                  </v-list-item-content>
                  <v-list-item-content class="align-end">
                      <v-switch
                          small
                          v-model="dockerEnableState"
                          :label="`${dockerEnableState ? 'Enabled' : 'Disabled'}`"
                      ></v-switch>
                  </v-list-item-content>
                  <v-list-item-icon>
                    <!-- just for alignment, not editable -->
                  </v-list-item-icon>
                </v-list-item>
                 <v-list-item v-if="device.info">
                  <v-list-item-content>
                    <span class="text-caption">Exec runtime</span>
                  </v-list-item-content>
                  <v-list-item-content class="align-end">
                      <v-switch
                          v-model="execEnableState"
                          :label="`${execEnableState ? 'Enabled' : 'Disabled'}`"
                      ></v-switch>
                  </v-list-item-content>
                  <v-list-item-icon>
                    <!-- just for alignment, not editable -->
                  </v-list-item-icon>
                </v-list-item>

                <v-card-text  v-if="
                    device !== null &&
                    device.info &&
                    device.info.docker &&
                    device.info.docker.version !== ''
                  ">
                  Container runtime
                  <v-list dense>
                    <v-list-item>
                      <v-list-item-content>
                        <span class="font-weight-bold">Docker version</span>
                      </v-list-item-content>
                      <v-list-item-content class="align-end">
                        {{ device.info.docker.version }}
                      </v-list-item-content>
                      <v-list-item-icon>
                        <!-- just for alignment, not editable -->
                      </v-list-item-icon>
                    </v-list-item>

                    <v-list-item>
                      <v-list-item-content>
                        <span class="font-weight-bold">Bridge IP</span>
                      </v-list-item-content>
                      <v-list-item-content class="align-end">
                        {{ device.info.docker.bridgeIP }}
                      </v-list-item-content>
                      <v-list-item-icon>
                        <!-- just for alignment, not editable -->
                      </v-list-item-icon>
                    </v-list-item>

                    <v-list-item>
                      <v-list-item-content>
                        <span class="font-weight-bold">OS Type</span>
                      </v-list-item-content>
                      <v-list-item-content class="align-end">
                        {{ device.info.docker.osType }}
                      </v-list-item-content>
                      <v-list-item-icon>
                        <!-- just for alignment, not editable -->
                      </v-list-item-icon>
                    </v-list-item>

                    <v-list-item>
                      <v-list-item-content>
                        <span class="font-weight-bold">Privileged Enabled</span>
                      </v-list-item-content>
                      <v-list-item-content class="align-end">
                        {{ device.info.docker.privilegedEnabled }}
                      </v-list-item-content>
                      <v-list-item-icon>
                        <!-- just for alignment, not editable -->
                      </v-list-item-icon>
                    </v-list-item>

                    <v-list-item>
                      <v-list-item-content>
                        <span class="font-weight-bold">Runtimes</span>
                      </v-list-item-content>
                      <v-list-item-content class="align-end">
                        {{ device.info.docker.runtimes }}
                      </v-list-item-content>
                      <v-list-item-icon>
                        <!-- just for alignment, not editable -->
                      </v-list-item-icon>
                    </v-list-item>

                    <v-list-item>
                      <v-list-item-content>
                        <span class="font-weight-bold">Health</span>
                      </v-list-item-content>
                      <v-list-item-content class="align-end">
                        {{ device.info.docker.health }}
                      </v-list-item-content>
                      <v-list-item-icon>
                        <!-- just for alignment, not editable -->
                      </v-list-item-icon>
                    </v-list-item>

                    <v-list-item>
                      <v-list-item-content>
                        <span class="font-weight-bold">Health Description</span>
                      </v-list-item-content>
                      <v-list-item-content class="align-end">
                        {{ device.info.docker.healthDescription }}
                      </v-list-item-content>
                      <v-list-item-icon>
                        <!-- just for alignment, not editable -->
                      </v-list-item-icon>
                    </v-list-item>
                  </v-list>
                </v-card-text>

                <v-card-text>
                  Synpse agent
                  <v-list dense>
                    <v-list-item v-if="device && device.info && device.info.agentLogLevel">
                      <v-list-item-content>
                        <span class="font-weight-bold">Log level</span>
                      </v-list-item-content>
                      <v-list-item-content class="align-end">
                        {{ device.info.agentLogLevel }}
                      </v-list-item-content>
                      <v-list-item-icon>
                        <!-- just for alignment, not editable -->
                      </v-list-item-icon>
                    </v-list-item>

                    <v-list-item v-if="device && device.info && device.info.agentVersion">
                      <v-list-item-content>
                        <span class="font-weight-bold">Agent version</span>
                      </v-list-item-content>
                      <v-list-item-content class="align-end">
                        {{ device.info.agentVersion }}
                      </v-list-item-content>
                      <v-list-item-icon>
                        <!-- just for alignment, not editable -->
                      </v-list-item-icon>
                    </v-list-item>
                  </v-list>
                </v-card-text>
              </v-expansion-panel-content>
            </v-expansion-panel>
          </v-expansion-panels>
        </v-card>
      </v-flex>
    </v-layout>
  </div>
</template>

<style lang="stylus">
.v-expansion-panel-content__wrap {
  padding: 0 !important;
}

.line-horizontal, .line-vertical {
  display: none;
}
</style>

<script>
import PageSubtitle from '../PageSubtitle';
import DeviceLabelsEdit from './DeviceLabelsEdit';
import DeviceEnvVarsEdit from './DeviceEnvVarsEdit';
import DeviceApplications from './DeviceApplications';
import DeviceOperations from './DeviceOperations.vue';

export default {
  components: {
    PageSubtitle,    
    DeviceLabelsEdit,
    DeviceEnvVarsEdit,
    DeviceApplications,
    DeviceOperations
  },
  data() {
    return {
      warningMessage: "",
      dialog: false,
      versionDialog: false,
      editedItem: {},
      dockerEnableState: true,
      execEnableState: true,

      deviceTunnel: false,
      deviceTunnelLocalPort: 80,

      deviceTunnelLocalPortRules: [
        value => !!value || 'Required.',
        value => (value || '') >  1 || 'Port cannot be lower than 1',
        value => (value || '') <  65535 || 'Port cannot be higher than 65535'
      ],

      chartData: [
        {
          name: "CPU Utilization",
          chartType: 'line',
          values: [15, 20, -3, -15, 58, 12, -17, 37]
        }]
    }
  },

  watch: {
    dialog(val) {
      val || this.close()
    },
    versionDialog(val) {
      val || this.close()
    },
    dockerEnableState(val) {
      this.editedItem = Object.assign({}, this.device)
      this.editedItem.dockerRuntimeDisabled = !val
      this.save()
    },
    execEnableState(val) {
      this.editedItem = Object.assign({}, this.device)
      this.editedItem.execRuntimeDisabled = !val
      this.save()
    }
  },

  computed: {
    breadcrumbItems() {
      return [
        {
          text: 'Devices',
          disabled: false,
          to: {
            name: 'devices',
            query: {
              pageToken: this.pagination.currentPageToken,
            },
          },
        },
        {
          text: this.device ? this.device.name : '',
          disabled: true,
        },
      ]
    },
    projectId() {
      return this.$route.params.project
    },
    deviceId() {
      return this.$route.params.device
    },
    device() {
      return this.$store.state.device.device
    },
    deviceTunnelEnabled() {
      if (!this.device) {
        return false
      }

      if (this.device.tunnels && this.device.tunnels?.internal && this.device.tunnels.internal?.length > 0) {
        // We have one tunnel configured
        return true
      }

      return false
    },
    dockerEnabled() {
      if (this.device == undefined || this.device.agentSettings == undefined) {
        return false
      }
      return !this.device.agentSettings.dockerRuntimeDisabled
    },
    execEnabled() {
      if (this.device == undefined || this.device.agentSettings == undefined) {
        return false
      }
      return !this.device.agentSettings.execRuntimeDisabled
    },
    deviceTunnelPublicURL() {
      if (!this.deviceTunnelEnabled) {
        return ''
      }

      return this.device.tunnels.internal[0].url
    },
    // currentDeviceTunnelLocalPort returns current device local port
    // from a configured tunnel
    currentDeviceTunnelLocalPort() {
      if (!this.deviceTunnelEnabled) {
        // Returning same as we would set if it's not there
        return this.deviceTunnelLocalPort
      }

      return this.device.tunnels.internal[0].localPort
    },
    availableDeviceVersions() {
      return this.$store.state.device.versions
    },
    metrics() {
      return this.$store.state.device.deviceMetrics
    },
    utilizationMetricLabels() {
      let labels = ['']
      for (var metric of this.$store.state.device.deviceMetrics) {
        labels.push(metric.timestamp);
      }
      return labels
    },
    cpuUtilizationMetricChartData() {
      let chartData = {
      name: "CPU Utilization",
        chartType: 'line',
        regionFill: 1,
        values: []
      }

      for (var metric of this.$store.state.device.deviceMetrics) {
        chartData.values.push(metric.cpuPercent.toFixed(2))
      }
      return [chartData]
    },
    ramUtilizationMetricChartData() {
      let chartDataUsedRAM = {
        name: "Used",
        chartType: 'line',
        regionFill: 1,
        values: []
      }

      let chartDataTotalRAM = {
        name: "Total",
        chartType: 'line',
        regionFill: 1,
        values: []
      }

      for (var metric of this.$store.state.device.deviceMetrics) {
        chartDataUsedRAM.values.push((metric.memPercent * metric.memTotal / 100).toFixed(2))
        chartDataTotalRAM.values.push(metric.memTotal)
      }

      return [chartDataUsedRAM, chartDataTotalRAM]
    },
    gpuUtilizationMetricChartData() {
      if (!this.device) {
        return []
      }
      // return only if device is defined and has gpus
      if (this.device == undefined || this.device.info == undefined ||
          this.device.info.gpusInfo == undefined || this.device.info.gpusInfo.length == 0 || this.device.info.gpusInfo[0].metadata == null) {
            return []
      }

      let chartData = {
        name: "GPU Utilization",
        chartType: 'line',
        regionFill: 1,
        values: []
      }

      for (var metric of this.$store.state.device.deviceMetrics) {
        chartData.values.push(metric.gpuUtilization)
      }
      return [chartData]
    },
    gpuMemoryUtilizationMetricChartData() {
      // return only if device is defined and has gpus
      if (this.device == undefined || this.device.info == undefined ||
          this.device.info.gpusInfo == undefined || this.device.info.gpusInfo.length == 0 || this.device.info.gpusInfo[0].metadata == null) {
            return []
      }

      let chartData = {
        name: "Used",
        chartType: 'line',
        regionFill: 1,
        values: []
      }

      let chartDataTotal = {
        name: "Total",
        chartType: 'line',
        regionFill: 1,
        values: []
      }

      for (var metric of this.$store.state.device.deviceMetrics) {
        // convert metric.gpuMemPercent to MiB
        // chartData.values.push(metric.gpuMemPercent)
        if (metric.gpuMemTotal == 0 || metric.gpuMemPercent == 0) {
          chartData.values.push(0)
          chartDataTotal.values.push(0)
          continue
        }

        chartData.values.push((metric.gpuMemPercent * metric.gpuMemTotal / 100).toFixed(2))

        chartDataTotal.values.push(metric.gpuMemTotal)
      }

      return [chartData, chartDataTotal]
    },
    loading() {
      return this.$store.state.device.loading
    },
    error() {
      return this.$store.state.device.error
    },
    pagination() {
      return this.$store.state.device.devicesPagination
    }
  },

  mounted() {
    this.subscribe()
    this.refresh()
    this.getAvailableDeviceVersions()
  },

  beforeDestroy() {
    this.$store.dispatch('UnsubscribeFromSSE')
    this.$store.dispatch('ResetDeviceMetrics')
  },

  beforeUnmount() {
    this.$store.dispatch('UnsubscribeFromSSE')
    this.$store.dispatch('ResetDeviceMetrics')
  },

  methods: {
    openSSH() {
      if (this.device && this.device.status === 'online') {
        this.$store.dispatch('OpenSSHSession', {
          deviceId: this.device.id,
          deviceName: this.device.name,
          projectId: this.device.projectId
        })
      }
    },
    copyToClipboard(val) {
      this.$store.dispatch('Notify', `Copied!`)
      this.$clipboard(val)
    },
    getAvailableDeviceVersions() {
      let q = {
        projectId: this.projectId
      }
      this.$store.dispatch('GetDeviceVersions', q)
    },
    getVersionName(item){
      if (item != undefined && item.tags != undefined) {
          return item.version +" - "+ item.tags.join(', ')
      } else {
          return "?"
      }
    } ,
    refresh() {
      let q = {
        projectId: this.projectId,
        deviceId: this.deviceId
      }
      this.$store.dispatch('GetDevice', q).then(() => {
        this.deviceTunnel = this.device.tunnels?.internal.length > 0 ? true : false

        this.deviceTunnelLocalPort = this.currentDeviceTunnelLocalPort
        this.dockerEnableState = this.dockerEnabled
        this.execEnableState = this.execEnabled
      })
      this.$store.dispatch('GetDeviceMetrics', q)
    },
    subscribe() {
      let token = this.$store.getters.jwt
      let streamUrl = `/api/projects/${this.projectId}/events?stream=${this.projectId}:devices:${this.deviceId}&_t=${token}`

      let q = {
        stream: streamUrl,
        callback: this.refresh
      }

      this.$store.dispatch('SubscribeToSSE', q)

    },
    close() {
      this.dialog = false
      this.versionDialog = false
      this.$nextTick(() => {
        this.editedItem = Object.assign({}, this.device)
      })
    },
    editName() {
      this.editedItem = Object.assign({}, this.device)
      this.dialog = true
    },
    editDesiredAgentVersion() {
      this.editedItem = Object.assign({}, this.device)
      this.versionDialog = true
    },
    save() {
      // TODO: hack, fix UI
      this.editedItem.desiredAgentVersion = this.editedItem.desiredAgentVersion.version
      this.$store.dispatch('UpdateDevice', this.editedItem).then(() => {
        if (this.error) {
          return
        }
        this.close()
        this.refresh()
      })
    },
    selectedVersion(value){
      if (value.tags.includes("beta")) {
        this.warningMessage = "Upgrade to Beta version is not tested and can harm your device. Please test it on a test device before using it on production."
      } else{
        this.warningMessage = ""
      }
    },
    toggleDeviceTunnel() {
      let current =  this.device.tunnels?.internal?.length > 0 ? true : false

      if (!current) {
        // turning on the tunnel
        let payload = {
          projectId: this.device.projectId,
          deviceId: this.device.id,
          enabled: true,
          localPort: parseInt(this.deviceTunnelLocalPort, 10)
        }
        this.$store.dispatch('UpdateDeviceTunnels', payload).then(() => {
          if (this.error) {
            this.$store.dispatch('Notify', `${this.error}`)
          } else {
            this.refresh()
            this.$store.dispatch('Notify', `Tunnel enabled for port ${this.deviceTunnelLocalPort}`)
          }
        })
        return
      }

      // turning off the tunnel
      let payload = {
        projectId: this.device.projectId,
        deviceId: this.device.id,
        enabled: false,
        localPort: parseInt(this.deviceTunnelLocalPort, 10)
      }
      this.$store.dispatch('UpdateDeviceTunnels', payload).then(() => {
        if (this.error) {
          this.$store.dispatch('Notify', `${this.error}`)
        } else {
          this.refresh()
          this.$store.dispatch('Notify', `Tunnel disabled`)
        }
      })
    }
  }
}
</script>
